import { Component, computed, effect, HostListener, OnInit, signal, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { Role } from 'src/app/classes/auth/role.class';
import { CustomerProject } from 'src/app/classes/customer-project.class';
import { Customer } from 'src/app/classes/customer.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AfasService } from 'src/app/services/afas.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { UserService } from 'src/app/services/user/user.service';
import { debounce } from 'src/app/utils.class';
import { UserFormComponent } from '../user-form/user-form.component';

type UsersView = 'default' | 'external' | 'former';

interface UsersResponse {
    users: User[];
    total: number;
}

@Component({
    selector: 'app-users-overview',
    templateUrl: './users-overview.component.html',
    styleUrls: ['./users-overview.component.scss'],
    standalone: false,
})
export class UsersOverviewComponent extends CodaltComponent implements OnInit {
    @ViewChild(MatPaginator) paginator: MatPaginator;

    readonly pageSize = signal(this.calculatePageSizeValue());
    readonly pageIndex = signal(0);
    readonly showDeleted = signal(false);
    readonly view = signal<UsersView>('default');
    readonly search = signal('');
    readonly selectedRoleKeys = signal<string[]>([]);
    readonly isSyncing = signal(false);

    private readonly refreshIndex = signal(0);

    readonly rolesMap = toSignal(this.authorisationService.getRolesMap(), {
        initialValue: new Map<string, Role>(),
    });

    readonly roles = computed(() => Array.from(this.rolesMap().values()));

    readonly hasExternal = computed(() => this.roles().some(role => role.is_external));

    private readonly externalRoleKeys = computed(() =>
        this.roles()
            .filter(role => role.is_external)
            .map(role => role.key)
    );

    private readonly formerEmployeeRoleKeys = computed(() =>
        this.roles()
            .filter(role => role.key === 'formerEmployee')
            .map(role => role.key)
    );

    private readonly defaultRoleKeys = computed(() =>
        this.roles()
            .filter(role => !role.is_external && role.key !== 'formerEmployee')
            .map(role => role.key)
    );

    private hasInitialisedRoleFilter = false;

    readonly viewRoleKeys = computed(() => {
        switch (this.view()) {
            case 'external':
                return this.externalRoleKeys();

            case 'former':
                return this.formerEmployeeRoleKeys();

            case 'default':
            default:
                return this.defaultRoleKeys();
        }
    });

    readonly availableRoles = computed(() => {
        const viewRoleKeys = new Set(this.viewRoleKeys());

        return this.roles().filter(role => viewRoleKeys.has(role.key));
    });

    readonly activeRoleKeys = computed(() => {
        const selectedRoleKeys = this.selectedRoleKeys();

        return selectedRoleKeys.length ? selectedRoleKeys : this.viewRoleKeys();
    });

    readonly selectedRoleLabel = computed(() => {
        const selectedRoleKeys = this.selectedRoleKeys();

        if (!selectedRoleKeys.length) {
            return 'Alle rollen';
        }

        if (selectedRoleKeys.length === 1) {
            const roleKey = selectedRoleKeys[0];

            return this.rolesMap().get(roleKey)?.name || '1 rol';
        }

        return `${selectedRoleKeys.length} rollen`;
    });

    private readonly debouncedSearch = toSignal(
        toObservable(this.search).pipe(debounceTime(300), distinctUntilChanged()),
        {
            initialValue: '',
        }
    );

    private readonly usersFilters = computed(() => ({
        showDeleted: this.showDeleted(),
        search: this.debouncedSearch(),
        roleKeys: this.activeRoleKeys(),
        includeUsersWithoutRoles: this.view() === 'default' && !this.selectedRoleKeys().length,
    }));

    private readonly usersPagination = computed(() => ({
        page: this.pageIndex() + 1,
        pageSize: this.pageSize(),
        refreshIndex: this.refreshIndex(),
    }));

    private readonly usersResponse = toSignal(
        combineLatest([
            toObservable(this.usersFilters).pipe(
                distinctUntilChanged(
                    (a, b) =>
                        a.showDeleted === b.showDeleted &&
                        a.search === b.search &&
                        a.roleKeys.join(',') === b.roleKeys.join(',')
                )
            ),
            toObservable(this.usersPagination),
        ]).pipe(
            switchMap(([filters, pagination]) =>
                this.userService
                    .getPaginatedList(
                        pagination.page,
                        pagination.pageSize,
                        filters.showDeleted,
                        filters.search,
                        filters.roleKeys,
                        filters.includeUsersWithoutRoles
                    )
                    .pipe(
                        catchError(err => {
                            this.confirmDialog.confirm({
                                title: 'Fout bij ophalen gebruikers',
                                text: `Er is een fout opgetreden bij het ophalen van gebruikers: ${err.message || err}`,
                                confirm: 'OK',
                            });

                            return of({
                                users: [],
                                total: 0,
                            } satisfies UsersResponse);
                        })
                    )
            )
        ),
        {
            initialValue: {
                users: [],
                total: 0,
            } satisfies UsersResponse,
        }
    );

    readonly users = computed(() => this.usersResponse().users);
    readonly total = computed(() => this.usersResponse().total);

    constructor(
        private userService: UserService,
        private authorisationService: AuthorisationService,
        private confirmDialog: ConfirmDialogService,
        private afasService: AfasService,
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog
    ) {
        super();

        effect(() => {
            const roleKeys = this.viewRoleKeys();

            if (this.hasInitialisedRoleFilter || !roleKeys.length) {
                return;
            }

            this.selectedRoleKeys.set([]);
            this.hasInitialisedRoleFilter = true;
        });
    }

    ngOnInit(): void {
        const search = this.route.snapshot.queryParamMap.get('search');
        if (search) {
            this.search.set(search);
        }
    }

    @HostListener('window:resize')
    @debounce()
    onResize(): void {
        const nextPageSize = this.calculatePageSizeValue();

        if (nextPageSize === this.pageSize()) {
            return;
        }

        this.pageSize.set(nextPageSize);
        this.pageIndex.set(0);
    }

    onViewChange(view: UsersView): void {
        this.view.set(view);
        this.selectedRoleKeys.set([]);
        this.pageIndex.set(0);
    }

    onRoleFilterChange(roleKeys: string[]): void {
        this.selectedRoleKeys.set(roleKeys);
        this.pageIndex.set(0);
    }

    clearRoleFilter(event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();

        this.selectedRoleKeys.set([]);
        this.pageIndex.set(0);
    }

    onShowDeletedChange(showDeleted: boolean): void {
        this.showDeleted.set(showDeleted);
        this.pageIndex.set(0);
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
    }

    onSearchChange(search: string): void {
        this.search.set(search);
        this.pageIndex.set(0);
    }

    getUserUniqueRoles(user: User): Role[] {
        return this.getUserRoles(user).filter(role => role.is_unique);
    }

    getUserRegularRoles(user: User): Role[] {
        return this.getUserRoles(user).filter(role => !role.is_unique);
    }

    private getUserRoles(user: User): Role[] {
        return (user.roles || []).map(role => this.rolesMap().get(role.key)).filter((role): role is Role => !!role);
    }

    hasAllCustomerAccess(user: User): boolean {
        return AuthorisationService.hasFunctionality(Functionalities.AccessAllCustomers, user);
    }

    hasAllProjectAccess(user: User): boolean {
        return AuthorisationService.hasFunctionality(Functionalities.AllProjects, user);
    }

    getVisibleCustomers(user: User): Customer[] {
        return (user.customers || []).slice(0, 3);
    }

    getHiddenCustomerCount(user: User): number {
        return Math.max((user.customers || []).length - 3, 0);
    }

    getVisibleProjects(user: User): CustomerProject[] {
        return (user.customer_projects || []).slice(0, 3);
    }

    getHiddenProjectCount(user: User): number {
        return Math.max((user.customer_projects || []).length - 3, 0);
    }

    openAddUserModal(): void {
        const dialogRef = this.dialog.open(UserFormComponent, {
            width: '400px',
            disableClose: true,
        });

        this.subscriptions.add(
            dialogRef.afterClosed().subscribe(user => {
                if (user?.id) {
                    this.router.navigate(['gebruikers', user.id]);
                }
            })
        );
    }

    deleteUser(event: Event, user: User): void {
        event.preventDefault();
        event.stopPropagation();

        const userId = user.id;

        if (!userId) {
            return;
        }

        this.confirmDialog
            .confirm({
                title: 'Verwijderen',
                text: `Weet u zeker dat u de gebruiker ${user.name} wilt verwijderen?`,
                confirm: 'Ja',
                cancel: 'Behouden',
            })
            .then(() => {
                this.subscriptions.add(
                    this.userService.deleteUser(userId).subscribe({
                        next: () => {
                            this.refresh();
                        },
                        error: err => {
                            this.confirmDialog.confirm({
                                title: 'Fout bij verwijderen',
                                text: `Er is een fout opgetreden bij het verwijderen van gebruiker ${user.name}: ${err.message || err}`,
                                confirm: 'OK',
                            });
                        },
                    })
                );
            });
    }

    openUser(event: MouseEvent, user: User): void {
        if (!user) {
            user = new User();
        }

        if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            window.open(`/gebruikers/${user.id}`, '_blank');
        } else {
            this.router.navigate(['gebruikers', user.id]);
        }
    }

    sync(): void {
        this.isSyncing.set(true);

        this.subscriptions.add(
            this.afasService
                .afasSync()
                .pipe(
                    switchMap(() => this.userService.getList(true)),
                    finalize(() => this.isSyncing.set(false))
                )
                .subscribe({
                    next: () => {
                        this.refresh();
                    },
                    error: err => {
                        this.confirmDialog.confirm({
                            title: 'Fout bij synchronisatie',
                            text: `Er is een fout opgetreden bij het synchroniseren van gebruikers: ${err.message || err}`,
                            confirm: 'OK',
                        });
                    },
                })
        );
    }

    private refresh(): void {
        this.refreshIndex.update(value => value + 1);
    }

    private calculatePageSizeValue(): number {
        return Math.max(Math.floor((window.innerHeight - 200) / 42), 3);
    }
}
